import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "activity-log list-unstyled",
  "data-testing": "activity-log-container"
}
const _hoisted_2 = {
  class: "date",
  "data-testing": "blackout"
}
const _hoisted_3 = {
  class: "date",
  "data-testing": "blackout"
}
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { class: "event-text" }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = ["href"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { class: "has-text-right" }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "card-footer" }
const _hoisted_15 = { class: "card-footer-item" }

import {
  mdiCheck,
  mdiChevronDown,
  mdiChevronUp,
  mdiDelete,
  mdiPaperclip,
  mdiPlus
} from "@mdi/js";
import { computed, nextTick, PropType, ref } from "vue";

import { useContentStore } from "@/stores/content.store";
import { useUsersStore } from "@/stores/users.store";

import { ContentStructureSection } from "@/models/content-structure.model";
import {
  Message,
  SectionFile,
  StateTransition
} from "@/models/product-section.model";
import { OrganizationUser } from "@/models/user.model";
import { WorkflowState } from "@/models/workflow.model";

import { isSafeUrl } from "@/utils/file-helpers";
import { formatFullDate } from "@/utils/formatTimestamp";

import Collapse from "../ui/Collapse.vue";

interface Activity {
  displayDateTime: string;
  id: number;
  text: string;
  timestamp: number;
  type: string;
  userDisplayName: string;
  userId?: number;
  filename?: string;
  fileLocation?: string;
}

const activityLogLimit = 5;


export default /*@__PURE__*/_defineComponent({
  __name: 'ContentActivityLog',
  props: {
  isUpdating: {
    required: false,
    type: Boolean
  },
  section: {
    required: true,
    type: Object as PropType<ContentStructureSection>
  },
  sectionId: {
    required: true,
    type: Number
  },
  productId: {
    required: true,
    type: Number
  },
  workflowStates: {
    required: true,
    type: Array as PropType<WorkflowState[]>
  }
},
  setup(__props) {

const usersStore = useUsersStore();
const contentStore = useContentStore();

const props = __props;

const addingMessage = ref<boolean>(false);
const savingMessage = ref<boolean>(false);
const messageBeingDeleted = ref<number | undefined>(undefined);
const removeMessage = (messageId: number): void => {
  if (!messageBeingDeleted.value) {
    messageBeingDeleted.value = messageId;
  } else if (messageBeingDeleted.value === messageId) {
    contentStore.deleteMessage({
      messageId: messageId,
      productId: props.productId
    });
  }
};
const resetMessageDeletion = (): void => {
  messageBeingDeleted.value = undefined;
};
const saveMessage = (event: CustomEvent<{ textContent: string }>): void => {
  const message = event.detail.textContent;
  savingMessage.value = true;
  contentStore
    .submitMessage({
      productId: props.productId,
      sectionId: props.sectionId,
      message: message
    })
    .then((): void => {
      addingMessage.value = false;
      savingMessage.value = false;
    });
};
const newMessageInput = ref<HTMLElement | null>(null);
const allowMessage = (): void => {
  addingMessage.value = true;
  nextTick(() => {
    if (newMessageInput.value) {
      newMessageInput.value.focus();
    }
  });
};

const activityLogShowMore = ref<boolean>(false);
const toggleActivityLogShowMore = (): void => {
  activityLogShowMore.value = !activityLogShowMore.value;
};

const enableShowMore = ref<boolean>(false);
const messages = computed<Activity[]>(() => {
  return props.section.messages.map((message: Message): Activity => {
    const user = usersStore.users?.find(
      (e: OrganizationUser): boolean => e.id === message.user_id
    );
    return {
      displayDateTime: formatFullDate(message.updated_on_millis),
      timestamp: message.updated_on_millis,
      userDisplayName: user ? user.displayName : "",
      userId: message.user_id,
      text: message.message,
      type: "message",
      id: message.id
    };
  });
});
const transitionActivities = computed<Activity[]>(() => {
  const transitions: StateTransition[] = props.section.state_transitions.filter(
    (transition: StateTransition): boolean => {
      const newState: WorkflowState | undefined = props.workflowStates.find(
        (e: WorkflowState): boolean => e.id === transition.state_id
      );
      return !!newState;
    }
  );
  return transitions.map((tEvent: StateTransition): Activity => {
    const user: OrganizationUser | undefined = usersStore.users?.find(
      (e: OrganizationUser): boolean => e.id === tEvent.actor
    );
    const currentWorkflowState: WorkflowState | undefined =
      props.workflowStates.find(
        (e: WorkflowState): boolean => e.id === tEvent.state_id
      );
    const newStateName: string =
      currentWorkflowState && currentWorkflowState.name
        ? currentWorkflowState.name
        : "";
    return {
      displayDateTime: formatFullDate(tEvent.created_on_millis),
      timestamp: tEvent.created_on_millis,
      userDisplayName: user ? user.displayName : "",
      text: "Moved to " + newStateName,
      type: "transition",
      id: tEvent.id
    };
  });
});
const fileAttachments = computed<Activity[]>(() => {
  return props.section.files.map((file: SectionFile): Activity => {
    const user: OrganizationUser | undefined = usersStore.users?.find(
      (e: OrganizationUser): boolean => e.id === file.link_created_by
    );
    return {
      displayDateTime: formatFullDate(file.link_created_on),
      timestamp: file.link_created_on,
      userDisplayName: user ? user.displayName : "",
      userId: file.link_created_by,
      text: "",
      filename: file.name,
      fileLocation: file.location,
      type: "file-attached",
      id: file.file_to_section_id
    };
  });
});
const fileRemovals = computed<Activity[]>(() => {
  return props.section.files
    .filter((e: SectionFile): boolean => e.link_deleted_on)
    .map((file: SectionFile) => {
      const user = usersStore.users?.find(
        (e: OrganizationUser): boolean => e.id === file.link_created_by
      );
      return {
        displayDateTime: formatFullDate(
          file.link_deleted_on as unknown as number
        ),
        timestamp: file.link_deleted_on
          ? (file.link_deleted_on as unknown as number)
          : 0,
        userDisplayName: user ? user.displayName : "",
        userId: file.link_deleted_by
          ? (file.link_deleted_by as unknown as number)
          : 0,
        text: "Removed file: " + file.name,
        filename: file.name,
        fileLocation: file.location,
        type: "file-deleted",
        id: file.file_to_section_id
      };
    });
});
const componentAdditions = computed<Activity[]>(() => {
  return props.section.sections.map(
    (section: ContentStructureSection): Activity => {
      const user = usersStore.users?.find(
        (e: OrganizationUser): boolean => e.id === section.created_by
      );
      return {
        displayDateTime: formatFullDate(section.created_on_millis),
        timestamp: section.created_on_millis,
        userDisplayName: user ? user.displayName : "",
        userId: section.created_by,
        text: "Added " + section.type.name + ": " + section.name,
        type: "component-added",
        id: section.id
      };
    }
  );
});
const enableActivityLogShowMore = (arr: Activity[]): void => {
  enableShowMore.value = arr.length > activityLogLimit;
};
const activity = computed<Activity[]>(() => {
  const activityLogArr = [
    ...messages.value,
    ...transitionActivities.value,
    ...fileAttachments.value,
    ...fileRemovals.value,
    ...componentAdditions.value
  ].sort((a: Activity, b: Activity): number => {
    return b.timestamp - a.timestamp;
  });

  enableActivityLogShowMore(activityLogArr);
  return !activityLogShowMore.value
    ? activityLogArr.slice(0, activityLogLimit)
    : activityLogArr;
});

const getDateString = (): string => {
  return formatFullDate(Date.now());
};

return (_ctx: any,_cache: any) => {
  const _component_SVGIcon = _resolveComponent("SVGIcon")!
  const _directive_editable = _resolveDirective("editable")!

  return (_openBlock(), _createBlock(Collapse, {
    "is-content-empty": activity.value.length === 0 && !addingMessage.value,
    class: _normalizeClass({ updating: __props.isUpdating })
  }, {
    title: _withCtx(() => [...(_cache[2] || (_cache[2] = [
      _createTextVNode("Activity", -1)
    ]))]),
    content: _withCtx(() => [
      _createElementVNode("ul", _hoisted_1, [
        (addingMessage.value && _unref(usersStore).currentUser)
          ? (_openBlock(), _createElementBlock("li", {
              key: 0,
              class: _normalizeClass({ updating: savingMessage.value })
            }, [
              _createElementVNode("small", _hoisted_2, _toDisplayString(getDateString()), 1),
              _createElementVNode("small", null, _toDisplayString(_unref(usersStore).currentUser.displayName) + ":", 1),
              _withDirectives(_createElementVNode("span", {
                "data-testing": "new-message-text",
                id: "new-message",
                ref_key: "newMessageInput",
                ref: newMessageInput,
                class: "new-message event-text",
                onEdited: saveMessage
              }, null, 544), [
                [_directive_editable, true]
              ])
            ], 2))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activity.value, (event) => {
          return (_openBlock(), _createElementBlock("li", {
            tabindex: "0",
            class: _normalizeClass(["message-container", {
            updating: false
          }]),
            "data-testing": "event",
            key: event.timeStamp
          }, [
            _createElementVNode("small", _hoisted_3, _toDisplayString(event.displayDateTime), 1),
            _createElementVNode("div", null, [
              (event.userDisplayName)
                ? (_openBlock(), _createElementBlock("small", _hoisted_4, _toDisplayString(event.userDisplayName) + ": ", 1))
                : _createCommentVNode("", true),
              _createElementVNode("span", _hoisted_5, _toDisplayString(event.text), 1),
              (event.type === 'file-attached')
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                    _createVNode(_component_SVGIcon, { path: _unref(mdiPaperclip) }, null, 8, ["path"]),
                    _createElementVNode("a", {
                      href: 
                  event.fileLocation && _unref(isSafeUrl)(event.fileLocation)
                    ? event.fileLocation
                    : undefined
                ,
                      class: "btn-3",
                      target: "_blank"
                    }, _toDisplayString(event.filename), 9, _hoisted_7)
                  ]))
                : _createCommentVNode("", true),
              (
                event.type === 'message' &&
                _unref(usersStore).currentUser.id === event.userId
              )
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    "data-testing": "delete-message",
                    onClick: ($event: any) => (removeMessage(event.id)),
                    onBlur: _cache[0] || (_cache[0] = ($event: any) => (resetMessageDeletion())),
                    class: _normalizeClass([{ warning: messageBeingDeleted.value === event.id }, "delete-message btn-3 float-right"])
                  }, [
                    (messageBeingDeleted.value === event.id)
                      ? (_openBlock(), _createElementBlock("small", _hoisted_9, [
                          _createVNode(_component_SVGIcon, {
                            path: _unref(mdiCheck),
                            type: 'is-danger'
                          }, null, 8, ["path"]),
                          _cache[3] || (_cache[3] = _createTextVNode(" Confirm ", -1))
                        ]))
                      : (_openBlock(), _createElementBlock("small", _hoisted_10, [
                          _createVNode(_component_SVGIcon, { path: _unref(mdiDelete) }, null, 8, ["path"]),
                          _cache[4] || (_cache[4] = _createTextVNode(" Delete ", -1))
                        ]))
                  ], 42, _hoisted_8))
                : _createCommentVNode("", true)
            ])
          ]))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_11, [
        (enableShowMore.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn-3 float-right",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (toggleActivityLogShowMore()))
            }, [
              (activityLogShowMore.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_12, [
                    _createVNode(_component_SVGIcon, { path: _unref(mdiChevronUp) }, null, 8, ["path"]),
                    _cache[5] || (_cache[5] = _createTextVNode(" Show fewer ", -1))
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_13, [
                    _createVNode(_component_SVGIcon, { path: _unref(mdiChevronDown) }, null, 8, ["path"]),
                    _cache[6] || (_cache[6] = _createTextVNode(" Show more ", -1))
                  ]))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("footer", _hoisted_14, [
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("button", {
            class: "btn-3",
            type: "button",
            "data-testing": "add-message",
            onClick: allowMessage
          }, [
            _createVNode(_component_SVGIcon, { path: _unref(mdiPlus) }, null, 8, ["path"]),
            _cache[7] || (_cache[7] = _createTextVNode(" Add message ", -1))
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["is-content-empty", "class"]))
}
}

})